import { useState } from 'react';
import { Box, CheckboxGroup, Tooltip } from '@chakra-ui/react';
import { Colors, Checkbox, Button, Text } from '@mercator-gcp/grout';
import { Options, Option } from 'components/elements/Dropdown/FilterDropdown/types';
import { PhaseGroupedOptions } from 'components/elements/Dropdown/PhaseGroupedOptions';
import PopoverComponent from 'components/elements/Dropdown/PopoverComponent';
import { FlexRow } from 'components/layout/FlexRow';

interface ExclusionOptionsProps {
  options: Options;
  exclusionValues: any;
  setExclusionValues: any;
  onExclusionSubmit?: (values: string[], isParent: boolean) => void;
  exclusionChildValues?: any;
  setChildExclusionValues?: any;
  checkboxStyle: any;
  optionContainer: any;
  optionText: any;
  isOptionPartiallySelected: (option: Option) => boolean;
  groupByPhase?: boolean;
}

const ExclusionOptions = ({
  options,
  exclusionValues,
  setExclusionValues,
  onExclusionSubmit,
  exclusionChildValues,
  setChildExclusionValues,
  checkboxStyle,
  optionContainer,
  optionText,
  isOptionPartiallySelected,
  groupByPhase,
}: ExclusionOptionsProps) => {
  const [openPopovers, setOpenPopovers] = useState<Set<string>>(new Set());

  const handleChangeExclusion = (values: string[], isParent = true) => {
    const uniqueValues = Array.from(new Set(values)) as string[];
    setExclusionValues(uniqueValues);

    if (onExclusionSubmit) {
      onExclusionSubmit(uniqueValues, isParent);
    }
  };

  const selectAllExclusionChildren = (option: Option) => {
    const childrenValues: any = option.children?.map((child) => child?.value);
    const newValues = Array.from(
      new Set([...exclusionChildValues, ...childrenValues]),
    ) as string[];
    setChildExclusionValues(newValues);
    handleChangeExclusion(newValues, false);
  };

  const selectAllText = {
    pl: 'padding_8',
    pr: 'padding_8',
    pt: 'padding_4',
    position: 'sticky',
  };

  if (!groupByPhase) {
    return (
      <CheckboxGroup
        defaultValue={exclusionValues}
        value={exclusionValues}
        onChange={(values) => {
          handleChangeExclusion(values as string[], true);
        }}
      >
        {options.map((option) => {
          const optionKey = String(option.value);
          const isChildOpen = openPopovers.has(optionKey);

          const handlePopoverOpen = () => {
            setOpenPopovers((prev) => new Set(prev).add(optionKey));
          };

          const handlePopoverClose = () => {
            setOpenPopovers((prev) => {
              const next = new Set(prev);
              next.delete(optionKey);

              return next;
            });
          };

          return (
            <Box
              sx={{
                ...optionContainer,
                // Maintain hover state when child filter is open
                ...(isChildOpen && {
                  background: Colors.ArmyGreen100,
                }),
              }}
              key={`exclusion-option-${option.label}-${option.value}`}
            >
              <Checkbox
                key={option.value}
                value={option.value}
                sx={checkboxStyle}
                variant="negative"
                isPartialCheck={isOptionPartiallySelected(option)}
              >
                <Tooltip label={option.label} isTruncated shouldWrapChildren>
                  <Text as="div" sx={optionText}>
                    {option.label}
                  </Text>
                </Tooltip>
              </Checkbox>
              <FlexRow
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'space-between',
                }}
              >
                {option.children && (
                  <PopoverComponent
                    onOpen={handlePopoverOpen}
                    onClose={handlePopoverClose}
                  >
                    <>
                      <FlexRow sx={selectAllText}>
                        <Button
                          variant="unstyled"
                          onClick={() => {
                            selectAllExclusionChildren(option);
                          }}
                        >
                          <Text.Small color={Colors.TextQuiet}>Select all</Text.Small>
                        </Button>
                      </FlexRow>
                      <Box>
                        <CheckboxGroup
                          value={exclusionChildValues}
                          onChange={(values: string[]) => {
                            setChildExclusionValues(values);
                            handleChangeExclusion(values, false);
                          }}
                        >
                          {option.children.map((child) => (
                            <Checkbox
                              key={child.value}
                              value={child.value}
                              sx={optionContainer}
                              variant="negative"
                            >
                              <Tooltip
                                label={
                                  child.description ? child.description : child.label
                                }
                                shouldWrapChildren
                              >
                                <Text as="div" sx={optionText}>
                                  {child.label}
                                </Text>
                              </Tooltip>
                            </Checkbox>
                          ))}
                        </CheckboxGroup>
                      </Box>
                    </>
                  </PopoverComponent>
                )}
              </FlexRow>
            </Box>
          );
        })}
      </CheckboxGroup>
    );
  }

  return (
    <PhaseGroupedOptions
      options={options}
      variant="negative"
      checkedValues={exclusionValues}
      onChange={handleChangeExclusion}
      loading={false}
      onChildrenChange={(values: string[]) => {
        setChildExclusionValues(values);
        handleChangeExclusion(values, false);
      }}
      childValues={exclusionChildValues}
    />
  );
};

export default ExclusionOptions;
